import { LiveMobileViewContainer } from "@/components/slides/editor/LiveMobileViewContainer";
import { GradingTestModal } from "@/components/slides/editor/canvas/GradingTestModal";
import { useAppSelector } from "@/store/hooks";
import type { PhonicsDecodeSlideData } from "@ai-teach/types";
import { useRef, useState } from "react";
import { HiVolumeUp } from "react-icons/hi";
import { IoMic } from "react-icons/io5";

export function PhonicsDecodeLiveMobileView() {
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [gradingOpen, setGradingOpen] = useState(false);

  if (!slide) return null;

  const data = slide.slideData as PhonicsDecodeSlideData | undefined;
  const phoneme = data?.phoneme;
  const audioSrc = data?.audio?.src;

  const toggleAudio = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      void el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  return (
    <LiveMobileViewContainer>
      {/* Phoneme card — full-bleed, square, content centered; matches PhonicsDecode.tsx phonemeCard. */}
      <div
        className="flex min-h-[340px] w-full shrink-0 flex-col items-center justify-center gap-8 bg-white px-6 py-10"
        style={{ boxShadow: "0 6px 15px rgba(0,0,0,0.12)" }}
      >
        <div className="rounded-[15px] px-4 py-1.5" style={{ backgroundColor: "#FFE89F" }}>
          <span className="text-xl font-bold" style={{ color: "#727171" }}>
            Say the sound
          </span>
        </div>

        {phoneme ? (
          <span className="font-black leading-none" style={{ color: "#2626A5", fontSize: 70 }}>
            {phoneme}
          </span>
        ) : (
          <span className="text-sm italic text-gray-400">No phoneme set</span>
        )}

        {audioSrc && (
          // biome-ignore lint/a11y/useMediaCaption: phonics audio is pronunciation teaching
          <audio
            ref={audioRef}
            src={audioSrc}
            onEnded={() => {
              setPlaying(false);
              setProgress(0);
            }}
            onTimeUpdate={(e) => {
              const a = e.currentTarget;
              setProgress(a.duration ? a.currentTime / a.duration : 0);
            }}
          />
        )}
        {playing ? (
          <button
            type="button"
            onClick={toggleAudio}
            className="flex w-[300px] max-w-full items-center gap-3"
          >
            <HiVolumeUp className="shrink-0 text-[#FFAC27]" style={{ fontSize: 40 }} />
            <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#E5E5E5]">
              <div
                className="h-full rounded-full bg-[#FFAC27] transition-[width] duration-150"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
          </button>
        ) : (
          <button
            type="button"
            onClick={toggleAudio}
            disabled={!audioSrc}
            className="flex h-20 w-[300px] max-w-full items-center justify-center rounded-full transition-opacity disabled:opacity-40"
            style={{ backgroundColor: "#FFAC27" }}
          >
            <HiVolumeUp className="text-white" style={{ fontSize: 34 }} />
          </button>
        )}
      </div>

      <div className="flex-1" />

      {/* Record — the mic opens the grading test (the preview has no ASR pipeline). */}
      <div className="w-full flex justify-center pb-4 shrink-0">
        <button
          type="button"
          onClick={() => setGradingOpen(true)}
          className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg"
          style={{ background: "linear-gradient(180deg, #FF8500 0%, #FFB800 100%)" }}
        >
          <IoMic className="text-3xl" color="white" />
        </button>
      </div>

      <GradingTestModal
        isOpen={gradingOpen}
        onClose={() => setGradingOpen(false)}
        fields={[{ label: "Say the sound", value: phoneme }]}
        question={`Say the sound: ${phoneme ?? ""}`}
        correctAnswer={phoneme ?? ""}
        toastId="gradingToastPhonicsDecode"
      />
    </LiveMobileViewContainer>
  );
}
